// V4 — Mobile-first Availability view. Same data + write paths as
// Availability.jsx, but laid out as match-by-match cards. Self
// player surfaces big "Yes / Maybe / No" tap buttons + a drinks
// volunteer toggle. Elevated users get an expandable team roster
// underneath each match.

import React, { useMemo, useState } from "react";
import {
  CalendarRange, Wine, Check, ChevronDown, ChevronUp,
  MapPin, Clock, Users, Crown, History
} from "lucide-react";
import { useActiveTeam } from "../components/ActiveTeam";
import { useToast } from "../components/Toast";
import { api } from "../api";
import { fmtMatchTime } from "../utils/time";

const ELEVATED = ["ADMIN","PRESIDENT","VICE_PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"];
const STATUS_OPTIONS = [
  { value: "Available",     label: "Yes",   color: "var(--green)"  },
  { value: "Tentative",     label: "Maybe", color: "var(--orange)" },
  { value: "Not Available", label: "No",    color: "var(--red)"    }
];

function getUpcomingWeekend() {
  const now = new Date();
  const day = now.getDay();
  const sat = new Date(now);
  if (day === 0)      sat.setDate(sat.getDate() - 1);
  else if (day !== 6) sat.setDate(sat.getDate() + (6 - day));
  sat.setHours(0, 0, 0, 0);
  const sun = new Date(sat); sun.setDate(sun.getDate() + 1);
  const iso = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  return { startIso: iso(sat), endIso: iso(sun), satDate: sat, sunDate: sun };
}

function dayBadge(iso) {
  if (!iso) return "TBD";
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(`${iso}T00:00:00`);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "TODAY";
  if (diff === 1) return "TOMORROW";
  return d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
}
function dateLong(iso) {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function AvailabilityMobile({ state, reload, user, navigate }) {
  const toast = useToast();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const [busyKey, setBusyKey] = useState(null);
  // Admins / captains expand this section to record retroactive
  // attendance for completed games.
  const [showPast, setShowPast] = useState(false);

  const elevated = user?.roles?.some(r => ELEVATED.includes(r));
  const { startIso, endIso, satDate, sunDate } = useMemo(() => getUpcomingWeekend(), []);
  const todayIso = new Date().toISOString().slice(0, 10);

  const isSummerView = activeTeam?.code === "SUMMER_VOLLEYBALL";
  const matches = useMemo(() => {
    if (isSummerView) {
      return state.matches
        .filter(m => m.date >= todayIso && m.teamId === activeTeamId)
        .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))
        .slice(0, 2);
    }
    return state.matches
      .filter(m => m.date >= startIso && m.date <= endIso)
      .filter(m => isAllTeams || m.teamId === activeTeamId)
      .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  }, [state.matches, isSummerView, isAllTeams, activeTeamId, startIso, endIso, todayIso]);

  const pastMatches = useMemo(() => {
    return state.matches
      .filter(m => m.date < todayIso)
      .filter(m => isAllTeams || m.teamId === activeTeamId)
      .sort((a, b) => (b.date + (b.time || "")).localeCompare(a.date + (a.time || "")))
      .slice(0, 10);
  }, [state.matches, isAllTeams, activeTeamId, todayIso]);

  async function setStatus(matchId, userId, status) {
    const key = `${matchId}:${userId}`;
    const isSelf = userId === user.id;
    if (!isSelf && !elevated) return;
    setBusyKey(key);
    try {
      await api.setAvailability(matchId, userId, { status, comments: "" });
      await reload();
    } catch (err) { toast.error(err.message || "Couldn't update."); }
    finally { setBusyKey(null); }
  }
  async function setDrinks(matchId, userId, on) {
    const key = `drinks:${matchId}:${userId}`;
    const isSelf = userId === user.id;
    if (!isSelf && !elevated) return;
    setBusyKey(key);
    try {
      await api.setAvailability(matchId, userId, { volunteerDrinks: !!on });
      if (on) {
        try {
          const m = state.matches.find(mm => mm.id === matchId);
          if (m) {
            await api.createNotification({
              type: "VOLUNTEER_DRINKS",
              userId,
              message: `Thanks for volunteering for drinks for the ${m.sport} match vs ${m.opponent} on ${m.date}.`
            });
          }
        } catch (nErr) { console.warn("Notification queue failed:", nErr); }
      }
      await reload();
    } catch (err) { toast.error(err.message || "Couldn't update."); }
    finally { setBusyKey(null); }
  }

  const drinksDoneByUser = useMemo(() => {
    const s = new Set();
    for (const a of (state.availability || [])) {
      if (a.volunteerDrinks) s.add(a.userId);
    }
    return s;
  }, [state.availability]);

  return (
    <div className="m-avail">
      <header className="m-avail-head">
        <small>{isAllTeams ? "All Teams" : (activeTeam?.name || "Pitt Gabruz")}</small>
        <h1>Availability</h1>
        <p>
          {isSummerView
            ? "Next 2 Summer Volleyball games"
            : `${satDate.toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${sunDate.toLocaleDateString("en-US",{month:"short",day:"numeric"})}`}
        </p>
      </header>

      {matches.length === 0 && (
        <div className="m-avail-empty">
          <CalendarRange size={32}/>
          <p>{isSummerView
            ? "No upcoming Summer Volleyball games."
            : "No matches this weekend."}</p>
        </div>
      )}

      {matches.map(m => {
        const team = state.teams.find(t => t.id === m.teamId);
        const roster = (team?.players || [])
          .map(uid => state.members.find(u => u.id === uid))
          .filter(Boolean);
        return (
          <AvailabilityMatchCard
            key={m.id}
            m={m}
            team={team}
            roster={roster}
            availability={state.availability}
            drinksDoneByUser={drinksDoneByUser}
            user={user}
            elevated={elevated}
            busyKey={busyKey}
            onSetStatus={setStatus}
            onSetDrinks={setDrinks}
            onOpenSquad={() => navigate?.("squad")}
            isPast={false}
          />
        );
      })}

      {/* Past matches — admin-only retroactive RSVP recording. */}
      {elevated && (
        <button
          type="button"
          className="m-avail-pastToggle"
          onClick={() => setShowPast(v => !v)}
        >
          <History size={14}/>
          {showPast
            ? "Hide past matches"
            : `Show past matches (${pastMatches.length})`}
          {showPast ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
        </button>
      )}

      {elevated && showPast && pastMatches.length === 0 && (
        <div className="m-avail-empty">
          <CalendarRange size={28}/>
          <p>No past matches in scope.</p>
        </div>
      )}

      {elevated && showPast && pastMatches.map(m => {
        const team = state.teams.find(t => t.id === m.teamId);
        const roster = (team?.players || [])
          .map(uid => state.members.find(u => u.id === uid))
          .filter(Boolean);
        return (
          <AvailabilityMatchCard
            key={`past-${m.id}`}
            m={m}
            team={team}
            roster={roster}
            availability={state.availability}
            drinksDoneByUser={drinksDoneByUser}
            user={user}
            elevated={elevated}
            busyKey={busyKey}
            onSetStatus={setStatus}
            onSetDrinks={setDrinks}
            onOpenSquad={() => navigate?.("squad")}
            isPast={true}
          />
        );
      })}
    </div>
  );
}

function AvailabilityMatchCard({
  m, team, roster, availability, drinksDoneByUser,
  user, elevated, busyKey, onSetStatus, onSetDrinks, onOpenSquad,
  isPast = false
}) {
  // Past-match cards open with the roster expanded — admins land
  // there to retroactively log who showed up.
  const [showRoster, setShowRoster] = useState(isPast);

  // The logged-in user's own availability + drinks state for this match.
  const myAv = availability.find(a => a.matchId === m.id && a.userId === user.id);
  const myDrinks = !!myAv?.volunteerDrinks;
  const myAlreadyDrinks = drinksDoneByUser.has(user.id);
  const onRoster = roster.some(p => p.id === user.id);

  // Quick counts across the team
  const counts = useMemo(() => {
    const rows = availability.filter(a => a.matchId === m.id);
    return {
      a: rows.filter(r => r.status === "Available").length,
      t: rows.filter(r => r.status === "Tentative").length,
      n: rows.filter(r => r.status === "Not Available").length,
      total: roster.length
    };
  }, [m.id, availability, roster.length]);
  const responded = counts.a + counts.t + counts.n;
  const noResp = Math.max(0, counts.total - responded);

  return (
    <article className={`m-avail-card${isPast ? " m-avail-card--past" : ""}`}>
      {/* Day strip */}
      <div className="m-avail-card-dayStrip">
        {isPast && <span className="m-avail-card-pastBadge">PAST</span>}
        <span className="m-avail-card-day">{dayBadge(m.date)}</span>
        <span className="m-avail-card-date">{dateLong(m.date)}</span>
        <span className="m-avail-card-time"><Clock size={11}/> {m.time ? `${fmtMatchTime(m.time)} ET` : "TBD"}</span>
      </div>

      {/* Match heading */}
      <div className="m-avail-card-vs">
        <b>{(team?.name || "Pitt Gabruz").toUpperCase()}</b>
        <span className="m-avail-card-vsLabel">VS</span>
        <b>{m.opponent.toUpperCase()}</b>
      </div>
      {m.venue && (
        <div className="m-avail-card-venue"><MapPin size={11}/> {m.venue}</div>
      )}

      {/* Team-wide counts */}
      <div className="m-avail-card-counts">
        <span style={{color:"var(--green)"}}>{counts.a} Yes</span>
        <span style={{color:"var(--orange)"}}>{counts.t} Maybe</span>
        <span style={{color:"var(--red)"}}>{counts.n} No</span>
        <span style={{color:"var(--muted)"}}>{noResp} Pending</span>
      </div>

      {/* MY RSVP — hidden for past matches (only admins can rewrite
          retroactively, via the roster block below) */}
      {isPast ? (
        <div className="m-avail-pastNotice">
          Past match — admins / captains can record actual attendance via the roster below.
        </div>
      ) : onRoster ? (
        <div className="m-avail-myBlock">
          <small>YOUR RSVP</small>
          <div className="m-avail-rsvpBtns">
            {STATUS_OPTIONS.map(opt => {
              const active = myAv?.status === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`m-avail-rsvpBtn${active ? " m-avail-rsvpBtn--active" : ""}`}
                  style={{
                    "--accent": opt.color,
                    color: active ? "#111" : opt.color,
                    background: active ? opt.color : "transparent",
                    borderColor: opt.color
                  }}
                  disabled={busyKey === `${m.id}:${user.id}`}
                  onClick={() => onSetStatus(m.id, user.id, opt.value)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Drinks volunteer */}
          <div className="m-avail-drinks">
            {myDrinks ? (
              <>
                <span className="m-avail-drinks-tag">
                  <Check size={12}/> Volunteered for drinks
                </span>
                <button
                  type="button"
                  className="dark"
                  style={{padding:"6px 12px", fontSize:11}}
                  disabled={busyKey === `drinks:${m.id}:${user.id}`}
                  onClick={() => onSetDrinks(m.id, user.id, false)}
                >Undo</button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="dark"
                  style={{display:"inline-flex", alignItems:"center", gap:6, padding:"8px 12px", fontSize:12}}
                  disabled={busyKey === `drinks:${m.id}:${user.id}`}
                  onClick={() => onSetDrinks(m.id, user.id, true)}
                ><Wine size={13}/> Volunteer for drinks</button>
                {myAlreadyDrinks && (
                  <small style={{color:"var(--orange)"}}>Done earlier this season</small>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="m-avail-noRoster">
          You're not on {team?.name || "this team"}.
        </div>
      )}

      {/* Captain controls — collapsed roster list */}
      {elevated && roster.length > 0 && (
        <>
          <button
            type="button"
            className="m-avail-toggleRoster"
            onClick={() => setShowRoster(s => !s)}
          >
            <Users size={13}/>
            {showRoster ? "Hide" : "Show"} team roster ({roster.length})
            {showRoster ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
          </button>
          {showRoster && (
            <div className="m-avail-roster">
              {roster.map(p => {
                const av = availability.find(a => a.matchId === m.id && a.userId === p.id);
                const isSelf = p.id === user.id;
                const isCaptain = p.id === team?.captainId;
                const drinksHere = !!av?.volunteerDrinks;
                return (
                  <div key={p.id} className={`m-avail-rosterRow${isSelf ? " m-avail-rosterRow--self" : ""}`}>
                    <div className="m-avail-rosterRow-name">
                      <b>{p.name}</b>
                      {isCaptain && <Crown size={12} style={{color:"var(--gold)"}}/>}
                    </div>
                    <div className="m-avail-rosterRow-buttons">
                      {STATUS_OPTIONS.map(opt => {
                        const active = av?.status === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className="m-avail-miniBtn"
                            style={{
                              borderColor: opt.color,
                              color: active ? "#111" : opt.color,
                              background: active ? opt.color : "transparent"
                            }}
                            disabled={busyKey === `${m.id}:${p.id}`}
                            onClick={() => onSetStatus(m.id, p.id, opt.value)}
                            title={opt.label}
                          >
                            {opt.label.charAt(0)}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        className="m-avail-miniBtn"
                        style={{
                          borderColor: drinksHere ? "var(--green)" : "var(--line)",
                          color: drinksHere ? "var(--green)" : "var(--muted)"
                        }}
                        disabled={busyKey === `drinks:${m.id}:${p.id}`}
                        onClick={() => onSetDrinks(m.id, p.id, !drinksHere)}
                        title={drinksHere ? "Undo drinks volunteer" : "Mark drinks volunteer"}
                      >
                        <Wine size={11}/>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Footer link to squad */}
      <button
        type="button"
        className="dark m-avail-card-squadLink"
        onClick={onOpenSquad}
      >View squad for this match →</button>
    </article>
  );
}
